import { Helmet } from 'react-helmet-async';

export const SEO = ({ 
  title = 'RenoNovo - Appointment Generation for Home Service Contractors',
  description = 'RenoNovo helps home service contractors grow with exclusive appointments. Pool builders, kitchen remodeling, bathroom remodeling, roofing, HVAC & more. No shared leads.',
  keywords = 'appointment generation, home service contractors, pool builders, kitchen remodeling, bathroom remodeling, roofing leads, HVAC leads, contractor marketing',
  canonical,
  type = 'website',
  image = 'https://customer-assets.emergentagent.com/job_fresh-renovo/artifacts/q65wxm4z_Screenshot%202026-02-02%20at%203.57.11%E2%80%AFPM.png'
}) => {
  const siteUrl = 'https://renonovo.com';
  const fullUrl = canonical ? `${siteUrl}${canonical}` : siteUrl;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={fullUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="RenoNovo" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Additional SEO */}
      <meta name="robots" content="index, follow" />
      <meta name="googlebot" content="index, follow" />
      <meta name="author" content="RenoNovo, LLC" />
      <meta name="geo.region" content="US-FL" />
      <meta name="geo.placename" content="Satellite Beach" />
    </Helmet>
  );
};

export const LocalBusinessSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "RenoNovo, LLC",
    "description": "RenoNovo is an appointment generation company helping home service contractors grow with exclusive, qualified appointments. Serving pool builders, kitchen remodeling, bathroom remodeling, roofing, HVAC contractors and more.",
    "url": "https://renonovo.com",
    "telephone": "+1-321-210-2777",
    "email": "lead@renonovo.com",
    "foundingDate": "2007",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "1790 Hwy A1A, Suite 209",
      "addressLocality": "Satellite Beach",
      "addressRegion": "FL",
      "postalCode": "32937",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "28.1761",
      "longitude": "-80.5901"
    },
    "openingHours": "Mo-Fr 09:00-18:00",
    "sameAs": [],
    "serviceArea": {
      "@type": "Country",
      "name": "United States"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Appointment Generation Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Pool Builder Appointments"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Kitchen Remodeling Appointments"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Bathroom Remodeling Appointments"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Roofing Appointments"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "HVAC Appointments"
          }
        }
      ]
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

export const OrganizationSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "RenoNovo, LLC",
    "url": "https://renonovo.com",
    "logo": "https://customer-assets.emergentagent.com/job_fresh-renovo/artifacts/q65wxm4z_Screenshot%202026-02-02%20at%203.57.11%E2%80%AFPM.png",
    "description": "Appointment generation company for home service contractors",
    "telephone": "+1-321-210-2777",
    "email": "lead@renonovo.com",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "1790 Hwy A1A, Suite 209",
      "addressLocality": "Satellite Beach",
      "addressRegion": "FL",
      "postalCode": "32937",
      "addressCountry": "US"
    },
    "foundingDate": "2007",
    "areaServed": "United States"
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

export const FAQSchema = ({ faqs }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

export const BreadcrumbSchema = ({ items }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": `https://renonovo.com${item.path}`
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};
